<template>
  <div class="salaries-details">

    <div class="salaries-details__header">
      <ui-wad class="d-flex gap-2">
        <ui-link-button-meek
          :size="UI.SIZE.SMALL"
          :route="parentRoute"
          class="salaries-details__header__go-back"
        >
          <ui-icon
            name="navigate_before"
            class="salaries-details__header__go-back__icon"
          />
          Liste salariés
        </ui-link-button-meek>

        <transition-group
          enter-active-class="animate__animated animate__fadeIn ease-in-out a3 absolute"
        >
        <ui-tooltip-wrapper
          v-if="fetchState.status.value === 'success' && details?.acces_transat_actif"
          title="Ce salarié dispose d'un compte Transat"
        >
          <img
            class="salaries-details__header__transat"
            :src="'/images/brand/LOGOS_GHS_CMJN_GHS_TRANSAT_ICÔNE_R.svg'"
          >
        </ui-tooltip-wrapper>

        <div
          v-if="fetchState.status.value === 'success'"
          class="salaries-details__header__title"
        >
          <div>
            {{ details?.code_societe }} &mdash;
          </div>
          <div class="salaries-details__header__lastname">
            {{ details?.nom }}
          </div>
          <div class="salaries-details__header__firstname">
            {{ details?.prenom }} &mdash;
          </div>
          <div>
            {{ details?.matricule }}
          </div>
        </div>
        </transition-group>
      </ui-wad>


      <div class="salaries-details__header__actions">
        <ui-tooltip-wrapper
          title="Bientôt disponible"
        >
          <layout-cta-sub
            component="ui-button"
            :variant="UI.VARIANT.PRIMARY"
            :status="UI.STATUS.DISABLED"
          >
            Enregistrer
          </layout-cta-sub>
        </ui-tooltip-wrapper>
      </div>
    </div>

    <layout-nav-tab-menu
      v-model="selectedTab"
      class="salaries-details__tab-menu"
      :items="DETAILS_TABS_ITEMS"
    />


      <div
        v-if="selectedTab === TAB_MENU.DETAIL"
        class="salaries-details__tabs"
      >
        <transition-group
          class="relative"
          enter-active-class="animate__animated animate__fadeIn ease-in-out a3 absolute"
        >
          <details-tab
            v-if="details"
            :key="details.id"
            :salarie="details"
          />
        </transition-group>
      </div>

      <div
        v-if="fetchState.status.value === 'error'"
        class="salaries-details__error"
      >
        <ui-wad class="font-weight-light color-gray-dark">Les informations de ce salarié n'ont pas pu être chargées, veuillez réessayer</ui-wad>
        <ui-well
          :variant="UI.VARIANT.DANGER"
        >
          Cause technique: {{  errorMessage }}
        </ui-well>
      </div>

  </div>
</template>

<script lang="ts">
import { useAccessControl } from '~/composables/layout/access-control';
import { useCompanyOptionalNavigationContext } from '~/composables/layout/navigation';
import { DETAILS_TABS_ITEMS, TAB_MENU, useStore_salaries_details, type SalarieDetails } from '~/stores/salaries/details';
import { getSalariesRoute } from '~/stores/salaries/layout';
import { MENU_ITEM, NAVIGATION_SCOPE } from '~/utils/lib/layout/types';
import DetailsTab from '~/components/salaries/details/details-tab/index.vue'
import { useLegacyPageFetch } from '~/composables/core/hook';
import { useToastBuilder } from '~/composables/layout/toast';
import { tapCatch, toString } from '~/utils/lib/core/prelude';
import Console from '~/utils/lib/core/console';

const console = new Console('page:salarie-details')

export default defineNuxtComponent({
  components: {
    DetailsTab,
  },
  setup() {
    definePageMeta({
      navigationScope: NAVIGATION_SCOPE.COMPANY_OPTIONAL,
      menuItem: MENU_ITEM.SALARIES
    })

    const {
      fetchState,
      fetchWrapper,
    } = useLegacyPageFetch()

    const ToastBuilder = useToastBuilder()

    const navigationContext = useCompanyOptionalNavigationContext()
    const store_salaries_details = useStore_salaries_details()

    return {
      ...navigationContext,
      ...useAccessControl(),
      fetchState,
      fetchWrapper,
      store_salaries_details,
      ToastBuilder,
    }
  },
  data() {
    return {
      selectedTab: 'detail',
      errorMessage: '',
      details: null as SalarieDetails | null,
      DETAILS_TABS_ITEMS,
      TAB_MENU,
    }
  },
  computed: {
    parentRoute() {
      const currentLevel = this.fromCurrentLevel()
      return getSalariesRoute(
        currentLevel.structure.id,
        currentLevel.kind === 'COMPANY' ? currentLevel.company.code_societe : '',
      )
    },
  },
  created() {
    this.fetchWrapper(
      () => this.fetch()
    )
  },
  methods: {
    fetch() {
      const uuidData = this.fromCurrentLevel().structure.uuidData
      const salarieId = this.$route.params.id as string
      const societeId = this.$route.params.societe_id as string

      return this.store_salaries_details.get({
        uuidData,
        societeId,
        salarieId
      })
        .then((details) => {
          this.details = details
        })
        .catch(tapCatch(err => {
          console.warn(err)

          this.errorMessage = toString(err)
        }))
    }
  }
})

</script>

<style lang="sass" scoped>
@import '~/assets/sass/modules/layout/members'
@import '~/assets/sass/modules/table/members'
@import ~/assets/sass/modules/layout/variables
@import '~/assets/sass/modules/layout/members'
@import ~/assets/sass/modules/paie/variables

//empirical to align header
$offset-x: -5px
//empirical to align icon and slot
$offset-y: -2px

.salaries-details
  height: 100%
  display: flex
  flex-direction: column
  padding-bottom: space-x(5)

  &__error
    display: flex
    align-items: center
    flex-direction: column
    justify-content: center
    gap: space-x(2)
    height: 100%

  &__header
    padding: ($layout-spacer-y * .5) ($layout-spacer-x * .75) ($layout-spacer-y * .5)
    padding-bottom: 0
    background-color: $subheader-bg
    font-size: 14px
    display: flex
    justify-content: space-between

    &__transat
      height: 30px

    &__go-back
      align-self: center

      &__icon
        font-size: 120%
        margin-left: $offset-x
        margin-top: $offset-y


    &__title
      display: flex
      gap: space-x(1)
      font-weight: $font-weight-bolder
      line-height: 1
      align-items: center
      font-size: 130%
      text-align: center

    &__lastname
      text-transform: uppercase
      letter-spacing: 4%

    &__firstname
      text-transform: capitalize

    &__actions
      display: flex
      gap: space-x(1)
      align-items: center

  &__tab-menu
    padding-left: $offset-x
    padding-top: ($layout-spacer-y * .5)

  &__tabs
    margin: $card-spacer-y $card-spacer-x 0
</style>
